import { useState } from "react";
import { useZero } from "@rocicorp/zero/react";
import { mutators } from "@/lib/mutators";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ToolbarIconButton } from "@/components/ui/toolbar-icon-button";
import { CheckCircle, PencilSimple, Check, X } from "@/components/ui/icons";

/**
 * Q&A affordances for a thread in a PUBLIC community channel: an editable
 * title + solved badge in the header, and an accept/accepted control under
 * each reply. `canCurate` is the asker (or a manager/admin) in a public
 * workspace — the server re-checks authoritatively (mutators/thread-qa.ts).
 */

export function ThreadQaHeader({
  rootMessageId,
  title,
  solved,
  canCurate,
  canReopen,
}: {
  rootMessageId: string;
  title: string | null;
  solved: boolean;
  canCurate: boolean;
  /** Curator + an accepted answer is set (even if its reply was since deleted)
   *  → show the always-reachable Re-open control in the header. */
  canReopen: boolean;
}) {
  const zero = useZero();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const save = () => {
    zero.mutate(
      mutators.thread_qa.setThreadTitle({ rootMessageId, title: draft }),
    );
    setEditing(false);
  };

  if (editing) {
    return (
      <form
        className="flex min-w-0 flex-1 items-center gap-1"
        onSubmit={(e) => {
          e.preventDefault();
          save();
        }}
      >
        <Input
          autoFocus
          name="thread-title"
          autoComplete="off"
          aria-label="Thread title"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Give this question a title…"
          maxLength={300}
          className="h-7"
        />
        <ToolbarIconButton
          type="submit"
          title="Save title"
          aria-label="Save title"
        >
          <Check size={14} />
        </ToolbarIconButton>
        <ToolbarIconButton
          type="button"
          onClick={() => setEditing(false)}
          title="Cancel"
          aria-label="Cancel"
        >
          <X size={14} />
        </ToolbarIconButton>
      </form>
    );
  }

  return (
    <div className="flex min-w-0 items-center gap-2">
      <span className="truncate text-sm font-semibold">
        {title || "Thread"}
      </span>
      {solved && (
        <span className="flex shrink-0 items-center gap-1 rounded-full bg-success/15 px-2 py-0.5 text-[11px] font-medium text-success">
          <CheckCircle size={12} />
          Solved
        </span>
      )}
      {canCurate && (
        <ToolbarIconButton
          className="h-6 w-6"
          onClick={() => {
            setDraft(title ?? "");
            setEditing(true);
          }}
          title="Edit title"
          aria-label="Edit thread title"
        >
          <PencilSimple size={13} />
        </ToolbarIconButton>
      )}
      {canReopen && (
        <Button
          type="button"
          size="tiny"
          variant="ghost"
          className="shrink-0 text-muted-foreground hover:underline"
          onClick={() =>
            zero.mutate(
              mutators.thread_qa.clearAcceptedAnswer({ rootMessageId }),
            )
          }
          title="Re-open this question"
        >
          Re-open
        </Button>
      )}
    </div>
  );
}

export function ThreadReplyAcceptBar({
  rootMessageId,
  replyId,
  isAccepted,
  canCurate,
}: {
  rootMessageId: string;
  replyId: string;
  isAccepted: boolean;
  canCurate: boolean;
}) {
  const zero = useZero();
  if (!isAccepted && !canCurate) return null;

  if (isAccepted) {
    // The "Accepted answer" marker; re-opening lives in the header
    // (ThreadQaHeader) so it stays reachable even if this reply is deleted.
    return (
      <div className="ml-10 mb-1 flex items-center gap-2 text-xs font-medium text-success">
        <CheckCircle size={14} />
        Accepted answer
      </div>
    );
  }

  return (
    <div className="ml-10 mb-1">
      <Button
        size="tiny"
        variant="ghost"
        className="text-muted-foreground"
        onClick={() =>
          zero.mutate(
            mutators.thread_qa.acceptAnswer({
              rootMessageId,
              replyMessageId: replyId,
            }),
          )
        }
      >
        <CheckCircle size={13} />
        Accept as answer
      </Button>
    </div>
  );
}
